import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined"
import InputAdornment from "@mui/material/InputAdornment"
import MenuItem from "@mui/material/MenuItem"
import Tab from "@mui/material/Tab"
import Tabs from "@mui/material/Tabs"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { endOfDay, parseISO, startOfDay, subDays } from "date-fns"
import { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column, {
  CenterEverythingColumn,
  PaddedContentColumn,
} from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import PageTopSection from "../../../common/components/PageTopSection"
import Row, { RowButColumnOnMobile } from "../../../common/components/Row"
import TabContent from "../../../common/components/TabContent"
import TablePaginationRow from "../../../common/components/tables/TablePaginationRow"
import {
  usePaginationState,
  useTableSearchTextState,
} from "../../../common/components/tables/utils"
import { canUsePowerDialer } from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"
import PageContentContainer from "../components/PageContentContainer"
import ReportsPanel from "../reports"
import ActivityRecordCard, {
  ActivityRecordCardSkeleton,
} from "./ActivityRecordCard"
import ActivityTypeCapsule from "./ActivityTypeCapsule"
import PowerDialingHistoryTab from "./PowerDialingHistoryTab"
import {
  ACTIVITY_TABS,
  TYPE_FILTER_OPTIONS,
  buildInsightCards,
  getTypeFilterCount,
  resolveActivityTab,
} from "./utils"

function parseReportDateParam(reportDateParam) {
  if (!reportDateParam) {
    return null
  }
  try {
    const parsed = parseISO(reportDateParam)
    if (Number.isNaN(parsed.getTime())) {
      return null
    }
    return parsed
  } catch (_error) {
    return null
  }
}

export default function Activity() {
  const [searchParams, setSearchParams] = useSearchParams()
  const profile = useSelector(selectProfile)
  const showPowerDialing = canUsePowerDialer(profile)
  const tabFromUrl = searchParams.get("tab")
  const activeTab = resolveActivityTab(tabFromUrl, showPowerDialing)
  const reportDateParam = searchParams.get("report_date")
  const performedByParam = searchParams.get("performed_by") || ""
  const reportDateFromUrl = parseReportDateParam(reportDateParam)
  const [activities, setActivities] = useState([])
  const [performers, setPerformers] = useState([])
  const [typeCounts, setTypeCounts] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [selectedType, setSelectedType] = useState("")
  const [selectedActivityId, setSelectedActivityId] = useState(null)
  const [selectedPerformerId, setSelectedPerformerId] =
    useState(performedByParam)
  const [dateFrom, setDateFrom] = useState(() =>
    startOfDay(reportDateFromUrl || subDays(new Date(), 7))
  )
  const [dateTo, setDateTo] = useState(() =>
    endOfDay(reportDateFromUrl || new Date())
  )
  const {
    pageIndex,
    setPageIndex,
    totalResultsSize,
    setTotalResultsSize,
    pageSize,
    setPageSize,
  } = usePaginationState(20)
  const [searchText, setSearchText, debouncedSearchText] =
    useTableSearchTextState("q", setPageIndex)
  const axios = useAuthorizedAxiosClient()

  const insightSummary = useMemo(
    () => buildInsightCards(typeCounts, 0, activities),
    [typeCounts, activities]
  )

  useEffect(() => {
    const parsedReportDate = parseReportDateParam(reportDateParam)
    if (!parsedReportDate) {
      return
    }
    const nextFrom = startOfDay(parsedReportDate)
    const nextTo = endOfDay(parsedReportDate)
    setDateFrom((current) =>
      current?.getTime() === nextFrom.getTime() ? current : nextFrom
    )
    setDateTo((current) =>
      current?.getTime() === nextTo.getTime() ? current : nextTo
    )
  }, [reportDateParam])

  useEffect(() => {
    setSelectedPerformerId(performedByParam || "")
  }, [performedByParam])

  useEffect(() => {
    axios
      .get("/crm/api/activity/performers/")
      .then(({ data }) => setPerformers(data))
      .catch(() => setPerformers([]))
  }, [axios])

  useEffect(() => {
    if (activeTab !== ACTIVITY_TABS.activity) {
      return
    }
    setIsLoading(true)
    const params = {
      page: pageIndex,
      page_size: pageSize,
    }
    if (selectedType) {
      params.types = selectedType
    }
    if (selectedPerformerId) {
      params.performed_by = selectedPerformerId
    }
    if (debouncedSearchText) {
      params.q = debouncedSearchText
    }
    if (dateFrom) {
      params.date_from = dateFrom.toISOString()
    }
    if (dateTo) {
      params.date_to = dateTo.toISOString()
    }

    axios
      .get("/crm/api/activity/", { params })
      .then(({ data }) => {
        setActivities(data.results ?? [])
        setTotalResultsSize(data.count ?? 0)
        setTypeCounts(data.type_counts ?? {})
      })
      .catch(() => {
        setActivities([])
        setTotalResultsSize(0)
        setTypeCounts({})
      })
      .finally(() => setIsLoading(false))
  }, [
    axios,
    dateFrom,
    dateTo,
    debouncedSearchText,
    pageIndex,
    pageSize,
    selectedPerformerId,
    selectedType,
    setTotalResultsSize,
    activeTab,
  ])

  function handleTypeFilterChanged(typeValue) {
    setSelectedType((current) => (current === typeValue ? "" : typeValue))
    setSelectedActivityId(null)
    setPageIndex(1)
  }

  function handlePerformerFilterChanged(e) {
    setSelectedPerformerId(e.target.value)
    setPageIndex(1)
  }

  function handleDateFromChanged(date) {
    if (!date) {
      return
    }
    setDateFrom(startOfDay(date))
    setPageIndex(1)
  }

  function handleDateToChanged(date) {
    if (!date) {
      return
    }
    setDateTo(endOfDay(date))
    setPageIndex(1)
  }

  function handleTabChanged(_event, nextTab) {
    const next = new URLSearchParams(searchParams)
    if (nextTab === ACTIVITY_TABS.activity) {
      next.delete("tab")
    } else {
      next.set("tab", nextTab)
    }
    setSearchParams(next, { replace: true })
  }

  return (
    <PageContentContainer>
      <ConditionalPurpleLinearProgress
        visible={isLoading && activeTab === ACTIVITY_TABS.activity}
      />
      <PageTopSection title="Activity" />
      <PaddedContentColumn sx={{ gap: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChanged}>
          <Tab label="Activity" value={ACTIVITY_TABS.activity} />
          {showPowerDialing ? (
            <Tab label="Power Dialing" value={ACTIVITY_TABS.powerDialing} />
          ) : null}
          <Tab label="Reports" value={ACTIVITY_TABS.reports} />
        </Tabs>
        <TabContent value={activeTab} index={ACTIVITY_TABS.activity}>
          <Column gap={2}>
            <RowButColumnOnMobile sx={{ gap: 2, alignItems: "center" }}>
              <TextField
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search by candidate name"
                size="small"
                sx={{
                  minWidth: 280,
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "colors.white",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "colors.gray700",
                    opacity: 1,
                    fontWeight: 500,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon sx={{ color: "colors.gray700" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                size="small"
                label="Performed by"
                value={selectedPerformerId}
                onChange={handlePerformerFilterChanged}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">Anyone</MenuItem>
                {performers.map((performer) => (
                  <MenuItem key={performer.id} value={String(performer.id)}>
                    {performer.name}
                  </MenuItem>
                ))}
              </TextField>
              <DatePicker
                label="From"
                value={dateFrom}
                onChange={handleDateFromChanged}
                maxDate={dateTo}
                slotProps={{
                  textField: { size: "small", sx: { minWidth: 160 } },
                }}
              />
              <DatePicker
                label="To"
                value={dateTo}
                onChange={handleDateToChanged}
                minDate={dateFrom}
                maxDate={new Date()}
                slotProps={{
                  textField: { size: "small", sx: { minWidth: 160 } },
                }}
              />
            </RowButColumnOnMobile>
            <Row
              sx={{
                gap: 1,
                flexWrap: "wrap",
                alignItems: "center",
                width: "100%",
              }}
            >
              {TYPE_FILTER_OPTIONS.map((option) => (
                <ActivityTypeCapsule
                  key={option.label}
                  label={option.label}
                  count={getTypeFilterCount(option.value, insightSummary)}
                  selected={selectedType === option.value}
                  onClick={() => handleTypeFilterChanged(option.value)}
                />
              ))}
            </Row>
            {isLoading ? (
              <ActivityRecordCardSkeleton />
            ) : activities.length === 0 ? (
              <CenterEverythingColumn sx={{ py: 6 }}>
                <Typography variant="body1" color="text.secondary">
                  No activity.
                </Typography>
              </CenterEverythingColumn>
            ) : (
              <Column gap={1}>
                {activities.map((activity) => (
                  <ActivityRecordCard
                    key={activity.id}
                    activity={activity}
                    selected={selectedActivityId === activity.id}
                    onSelect={setSelectedActivityId}
                  />
                ))}
              </Column>
            )}
            <TablePaginationRow
              tableRows={activities}
              pageIndex={pageIndex}
              setPageIndex={setPageIndex}
              pageSize={pageSize}
              setPageSize={setPageSize}
              totalResultsSize={totalResultsSize}
            />
          </Column>
        </TabContent>
        {showPowerDialing ? (
          <TabContent value={activeTab} index={ACTIVITY_TABS.powerDialing}>
            <PowerDialingHistoryTab />
          </TabContent>
        ) : null}
        <TabContent value={activeTab} index={ACTIVITY_TABS.reports}>
          <ReportsPanel />
        </TabContent>
      </PaddedContentColumn>
    </PageContentContainer>
  )
}
